import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useWindowWidth } from '@react-hook/window-size'
import TransactionFilters from '../components/TransactionFilters'
import TransactionsTable from '../components/TransactionsTable'
import ActionConfirmationDialog from '../components/ActionConfirmationDialog'
import goalResultService from '../../../services/transactionService'
import moment from 'moment/moment'
import { LoadingSpinner } from '../../../components'

const filterOptions = [
  { name: 'All statuses', value: 'all' },
  { name: 'Deposit only', value: 'deposit' },
  { name: 'Withdrawal only', value: 'withdraw' },
]

const seedTransactions = [
  {
    id: 10021,
    transactionNo: 'BT-2024-00045',
    createdDate: '2025-09-28 14:23',
    remark: 'Bank transfer - slip verified',
    username: 'lena.c',
    email: 'lena.c@example.com',
    amount: 850.5,
    currency: 'USD',
    type: 'deposit',
    status: 'pending',
  },
  {
    id: 10022,
    transactionNo: 'BT-2024-00046',
    createdDate: '2025-09-28 15:12',
    remark: 'Requested manual withdrawal',
    username: 'nate_88',
    email: 'nate.88@example.com',
    amount: 420.0,
    currency: 'USD',
    type: 'withdraw',
    status: 'pending',
  },
  {
    id: 10023,
    transactionNo: 'BT-2024-00047',
    createdDate: '2025-09-28 17:45',
    remark: 'Crypto wallet top-up awaiting ledger',
    username: 'sakura',
    email: 'sakura@example.com',
    amount: 1200,
    currency: 'USD',
    type: 'deposit',
    status: 'pending',
  },
  {
    id: 10024,
    transactionNo: 'BT-2024-00048',
    createdDate: '2025-09-29 09:02',
    remark: 'AML trigger: requires manual review',
    username: 'marco_p',
    email: 'marco.p@example.com',
    amount: 2600,
    currency: 'USD',
    type: 'withdraw',
    status: 'pending',
  },
  {
    id: 10025,
    transactionNo: 'BT-2024-00049',
    createdDate: '2025-09-29 11:20',
    remark: 'Instant top-up via partner gateway',
    username: 'zoe-m',
    email: 'zoe.m@example.com',
    amount: 175,
    currency: 'USD',
    type: 'deposit',
    status: 'pending',
  },
]

const BalanceTransferPage = () => {
  const onlyWidth = useWindowWidth()
  const isMobile = onlyWidth <= 768;

  const [transactions, setTransactions] = useState([])
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[0])
  const [selectedAccount, setSelectedAccount] = useState();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(6)
  const [actionState, setActionState] = useState({
    open: false,
    type: null,
    transaction: null,
    note: '',
    transactionNo: '',
    amount: '',
  })
  const [saveLoading, setSaveLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const pendingTransactions = useMemo(
    () => transactions.filter((item) => item.status === 'pending'),
    [transactions],
  )

  useEffect(() => {
    fetchTransaction()
  }, [])

  const filteredTransactions = useMemo(() => {
    const trimmedSearch = searchTerm.trim().toLowerCase()
    return pendingTransactions
      .filter((item) => {
        if (selectedFilter.value === 'all') return true
        if (selectedFilter.value === 'deposit') return item.type === 'deposit'
        return item.type === 'withdraw'
      })
      .filter((item) => {
        if (!trimmedSearch) return true
        return (
          item.username.toLowerCase().includes(trimmedSearch) ||
          item.transactionNo.toLowerCase().includes(trimmedSearch) ||
          item.remark.toLowerCase().includes(trimmedSearch)
        )
      })
  }, [pendingTransactions, selectedFilter, searchTerm])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredTransactions.length / pageSize) || 1),
    [filteredTransactions.length, pageSize],
  )

  const currentPage = Math.min(page, totalPages)

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredTransactions.slice(start, start + pageSize)
  }, [filteredTransactions, currentPage, pageSize])

  const stats = useMemo(
    () => ({
      total: pendingTransactions.length,
      deposit: pendingTransactions.filter((item) => item.type === 'deposit').length,
      withdraw: pendingTransactions.filter((item) => item.type === 'withdraw').length,
    }),
    [pendingTransactions],
  )

  const handleFilterChange = (option) => {
    setSelectedFilter(option)
    setPage(1)
  }

  const handlePageChange = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages)
    setPage(clamped)
  }

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize)
    setPage(1)
  }

  const handleActionClick = (transaction, type) => {
    setActionState({
      open: true,
      type,
      transaction,
      note: '',
      transactionNo: '',
      amount: transaction?.amount != null ? String(transaction.amount) : '',
    })
  }

  const handleAmountChange = (value) => {
    setActionState((prev) => ({
      ...prev,
      amount: value,
    }))
  }

  const closeDialog = () => {
    setActionState((prev) => ({
      ...prev,
      open: false,
      type: null,
      transaction: null,
      note: '',
      transactionNo: '',
      amount: '',
    }))
    setError('');
  }

  const handleProceed = async () => {
    if (!actionState.transaction || !actionState.type) {
      return;
    }
    if (actionState.transaction.type === 'withdraw' && actionState.type === 'approve' && (!actionState.transactionNo || actionState.transactionNo.length !== 6)) {
      setError('Please enter valid last 6 transaction no.')
      return;
    }
    if (actionState.transaction.type === 'withdraw' && actionState.type === 'approve' && !selectedAccount) {
      setError('Please select bank account.')
      return;
    }
    if (actionState.type === 'reject' && !actionState.note) {
      setError('Please enter reason for rejection.')
      return;
    }

    setSaveLoading(true);
    setError('');

    const { transaction, type, note, transactionNo, amount } = actionState;
    const parsedAmount = amount ? Number(amount) : null;

    const payload = {
      status: type === 'approve' ? 'Approved' : 'Rejected',
      remark: note,
      transactionId: transaction.id,
      isAdmin: true,
      transactionNo: transactionNo || '',
      bankAccountId: selectedAccount?.value || 0,
      ...(parsedAmount !== null && Number.isFinite(parsedAmount) && transaction.type === "deposit" ? { amount: parsedAmount } : {})
    };

    //console.log("final payload", payload, actionState);

    try {
      const res = transaction.type === "deposit"
        ? await goalResultService.processDeposite(payload)
        : await goalResultService.processWithdrawal(payload);
      console.log("res", res);

      if (!res || res.status < 200 || res.status >= 300) {
        throw new Error('Non-success status returned');
      }

      if (res.data?.message) {
        //toast.success(`Transaction ${actionState.transaction.transactionNo} approved successfully.`)
        toast.success(res.data.message)
      }
      else if ( type === 'approve') {
        //toast.success(`Transaction ${actionState.transaction.transactionNo} approved successfully.`)
        toast.success(`Transaction approved successfully.`)
      } else {
        //toast.info(`Transaction ${actionState.transaction.transactionNo} rejected.`)
        toast.success(`Transaction rejected.`)
      }

      closeDialog();
      await fetchTransaction();
    } catch (error) {
      console.error('Failed to process transaction', error);
      toast.error('Something went wrong. Please try again later.')
    } finally {
      setSaveLoading(false);
    }
  }

  const fetchTransaction = async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        goalResultService.getDeposite(1),
        goalResultService.getWithdrawal(1),
        goalResultService.getBankAccount()
      ]);

      const allRequestsSuccessful = results.every(result => result.status === 200);
      if (!allRequestsSuccessful) {
        console.log("error");
        return;
      }

      const [deposit, withdraw, bankAccount] = results;
      const depositPayload = deposit?.data?.payload ?? [];
      const withdrawPayload = withdraw?.data?.payload ?? [];
      const bankAccountData = bankAccount?.data ?? [];

      if (depositPayload.length === 0 && withdrawPayload.length === 0) {
        setTransactions([]);
        setBankAccounts([]);
        setSelectedAccount(null);
        return;
      }

      const formattedAccounts = bankAccountData.map(account => ({
        name: `${account.bankAccountName} / ${account.bankAccountNumber}`,
        value: account.bankAccountId,
      }));

      console.log("bankAccount", formattedAccounts);
      setBankAccounts(formattedAccounts);
      setSelectedAccount((prevSelected) =>
        formattedAccounts.find(
          (account) => account.value === (prevSelected?.value ?? null)
        ) ?? formattedAccounts[0] ?? null
      );

      // Combine and format deposit and withdrawal data
      const combinedData = [...depositPayload, ...withdrawPayload];
      const formattedData = combinedData.map((v) => {
        return {
          id: v.transactionId,
          transactionNo: v.transactionNo,
          createdDate: moment(v.createdDate).format("DD MMM YYYY, HH:mm"),
          remark: v.userRemark,
          username: v.username,
          email: v.username,
          amount: v.amount,
          currency: 'USD',
          type: v.transactiontypeId === 1 ? 'deposit' : 'withdraw',
          status: 'pending',
          accountName: v.transactiontypeId === 1 ? v.agentAccountName : v.bankAccountName,
          accountNo: v.transactiontypeId === 1 ? v.agentAccountNo : v.bankAccountNo,
          bankType: v.transactiontypeId === 1 ? v.agentBankType : v.bankType,
        }
      })
      setTransactions(formattedData);
      console.log("combinedData", combinedData);
    } catch (error) {
      console.error('Failed to fetch transactions', error);
      toast.error('Unable to refresh transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-full flex-col gap-5">
      <TransactionFilters
        filterOptions={filterOptions}
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        stats={stats}
        isMobile={isMobile}
        onRefresh={fetchTransaction}
        refreshing={loading}
      />
      {
        loading ? <LoadingSpinner /> : 
        (
          <TransactionsTable
            data={paginatedTransactions}
            page={currentPage}
            pageSize={pageSize}
            totalItems={filteredTransactions.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onAction={handleActionClick}
            isMobile={isMobile}
          />)
      }
      {/* <section className="grid gap-4 rounded-md border border-dashed border-customAccent/40 bg-white p-4 text-sm shadow-custom dark:border-customAccent/30 dark:bg-customSurface">
        <h3 className="text-base font-semibold text-customPrimaryText dark:text-customHeading">
          Suggested next steps
        </h3>
        <ul className="grid gap-3 text-customSecondaryText dark:text-customMuted md:grid-cols-2">
          <li className="rounded-xl bg-customLightMilkWhite/90 p-3 font-medium text-customPrimaryText dark:bg-customSurfaceMuted dark:text-customLightMilkWhite">
            Enable auto-approval rules for deposits below a risk threshold to reduce manual work.
          </li>
          <li className="rounded-xl bg-customLightMilkWhite/90 p-3 font-medium text-customPrimaryText dark:bg-customSurfaceMuted dark:text-customLightMilkWhite">
            Allow bulk processing for high-volume periods with checklist confirmations.
          </li>
          <li className="rounded-xl bg-customLightMilkWhite/90 p-3 font-medium text-customPrimaryText dark:bg-customSurfaceMuted dark:text-customLightMilkWhite">
            Surface linked KYC status to speed up review decisions for new accounts.
          </li>
          <li className="rounded-xl bg-customLightMilkWhite/90 p-3 font-medium text-customPrimaryText dark:bg-customSurfaceMuted dark:text-customLightMilkWhite">
            Add export-to-CSV for compliance teams to track decision history every shift.
          </li>
        </ul>
      </section> */}

      <ActionConfirmationDialog
        bankAccounts={bankAccounts}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        open={actionState.open}
        type={actionState.type}
        transaction={actionState.transaction}
        note={actionState.note}
        error={error}
        transactionNo={actionState.transactionNo}
        amount={actionState.amount}
        onNoteChange={(value) =>
          setActionState((prev) => ({
            ...prev,
            note: value,
          }))
        }
        onNoChange={(value) =>
          setActionState((prev) => ({
            ...prev,
            transactionNo: value,
          }))
        }
        onAmountChange={handleAmountChange}
        onProceed={handleProceed}
        onCancel={closeDialog}
        saveLoading={saveLoading}
        isMobile={isMobile}
      />
    </div>
  )
}

export default BalanceTransferPage
